import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import { adminService } from '../../services/api';
import './styles.css';
import { useToast } from '../../contexts/ToastContext';
import ConfirmationModal from '../../components/ConfirmationModal';

const AdminUserDetails = () => {
  const { user_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [reports, setReports] = useState({ Beta: [], Alpha: [], 'Alpha+': [] });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ tier: 'Beta', report_type: '', report_uuid: '' });
  const [saving, setSaving] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [nextStatus, setNextStatus] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [tierModalOpen, setTierModalOpen] = useState(false);
  const [tierConfirmModalOpen, setTierConfirmModalOpen] = useState(false);
  const [nextTier, setNextTier] = useState('B');
  const [tierUpdating, setTierUpdating] = useState(false);
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        
        const data = await adminService.getUserDetails(user_id);
        if (!mounted) return;
        setUser(data.user);
        const incomingReports = data.reports || {};
        setReports({
          Beta: incomingReports.Beta || [],
          Alpha: incomingReports.Alpha || [],
          'Alpha+': incomingReports['Alpha+'] || []
        });
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load user');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user_id]);

  // Escape key handlers for modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showModal && !saving) {
          setShowModal(false);
        } else if (tierModalOpen && !tierUpdating) {
          setTierModalOpen(false);
        }
      }
    };

    if (showModal || tierModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
      };
    }
  }, [showModal, tierModalOpen, saving, tierUpdating]);

  return (
    <SidebarLayout>
      <div className="admin-user-details-container">
      <button onClick={() => navigate('/admin-dashboard')} className="admin-back-btn">Back</button>
      <h1 style={{ marginTop: 0 }}>User Details</h1>
      <div className="admin-actions-bar">
        {user && (
          <div
            className={`admin-status-chip ${user.is_active ? 'is-active' : 'is-inactive'}`}
            aria-live="polite"
          >
            <span className="admin-status-chip-dot" />
            {user.is_active ? 'Active user' : 'Inactive user'}
          </div>
        )}
        <div className="admin-actions-stack">
          <button
            className={`admin-toggle-status-btn ${user?.is_active ? 'deactivate' : 'activate'}`}
            onClick={() => {
              if (!user || statusUpdating) return;
              setNextStatus(!user.is_active);
              setStatusModalOpen(true);
            }}
            disabled={!user || statusUpdating}
          >
            {statusUpdating
              ? 'Updating...'
              : user?.is_active
              ? 'Deactivate user'
              : 'Activate user'}
          </button>
          <button
            className="admin-change-tier-btn"
            onClick={() => {
              if (!user || tierUpdating) return;
              setNextTier(user.product_tier || 'B');
              setTierModalOpen(true);
            }}
            disabled={!user || tierUpdating}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#374151',
              cursor: tierUpdating ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            {tierUpdating ? 'Updating...' : 'Change Tier'}
          </button>
          <button className="admin-add-report-btn" onClick={() => setShowModal(true)}>
            Add Report
          </button>
        </div>
      </div>
      {loading && (
        <div className="admin-user-details-card">
          <div className="admin-skeleton-grid">
            <div className="skeleton-block" />
            <div className="skeleton-block" />
            <div className="skeleton-block" />
            <div className="skeleton-block" />
            <div className="skeleton-block" />
            <div className="skeleton-block" />
          </div>
        </div>
      )}
      {error && <div style={{ color: 'crimson', marginBottom: 12 }}>{error}</div>}
      {user && (
        <div className="admin-user-details-card">
          <div className="admin-user-grid">
            <div className="admin-info-item">
              <div className="admin-info-label">Username</div>
              <div className="admin-info-value">{user.username || 'NA'}</div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">Email</div>
              <div className="admin-info-value">{user.email || 'NA'}</div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">Location</div>
              <div className="admin-info-value">{user.location || 'NA'}</div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">Mobile</div>
              <div className="admin-info-value">{user.mobile_number || 'NA'}</div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">Full Name</div>
              <div className="admin-info-value">{user.full_name || 'NA'}</div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">User ID</div>
              <div className="admin-info-value">{user.user_id}</div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">Status</div>
              <div className={`admin-info-value ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                {user.is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">Sector</div>
              <div className="admin-info-value">{user.sector_name || 'NA'}</div>
            </div>
            <div className="admin-info-item">
              <div className="admin-info-label">Product Tier</div>
              <div className="admin-info-value">
                <span style={{ 
                  padding: '4px 12px', 
                  borderRadius: '4px', 
                  backgroundColor: user.product_tier === 'A+' ? '#10b981' : user.product_tier === 'A' ? '#3b82f6' : '#6b7280',
                  color: 'white',
                  fontWeight: '500',
                  fontSize: '0.875rem'
                }}>
                  {user.product_tier || 'B'}
                </span>
              </div>
            </div>
            {user.entity_id && (
              <div className="admin-info-item">
                <div className="admin-info-label">Entity ID</div>
                <div className="admin-info-value">{user.entity_id}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="admin-reports-grid">
        <div className="admin-reports-section">
          <div className="admin-section-header tier-beta">
            <div className="admin-section-title">
              <span>Beta</span>
              <span className="admin-section-count">{reports.Beta.length}</span>
            </div>
          </div>
          {reports.Beta.map((r, idx) => (
            <div key={`b-${idx}`} className="admin-report-item">
              <span className="admin-report-dot"></span>
              <span>{r.report_type}</span>
            </div>
          ))}
          {reports.Beta.length === 0 && <div style={{ color: '#666' }}>No reports</div>}
        </div>

        <div className="admin-reports-section">
          <div className="admin-section-header tier-alpha">
            <div className="admin-section-title">
              <span>Alpha</span>
              <span className="admin-section-count">{reports.Alpha.length}</span>
            </div>
          </div>
          {reports.Alpha.map((r, idx) => (
            <div key={`a-${idx}`} className="admin-report-item">
              <span className="admin-report-dot"></span>
              <span>{r.report_type}</span>
            </div>
          ))}
          {reports.Alpha.length === 0 && <div style={{ color: '#666' }}>No reports</div>}
        </div>

        <div className="admin-reports-section">
          <div className="admin-section-header tier-alphap">
            <div className="admin-section-title">
              <span>Alpha+</span>
              <span className="admin-section-count">{reports['Alpha+'].length}</span>
            </div>
          </div>
          {reports['Alpha+'].map((r, idx) => (
            <div key={`ap-${idx}`} className="admin-report-item">
              <span className="admin-report-dot"></span>
              <span>{r.report_type}</span>
            </div>
          ))}
          {reports['Alpha+'].length === 0 && <div style={{ color: '#666' }}>No reports</div>}
        </div>
      </div>

      {showModal && (
        <div className="admin-modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div>Add Report</div>
              <button className="admin-btn admin-btn-secondary" onClick={() => !saving && setShowModal(false)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-modal-row">
                <label>Tier</label>
                <select
                  className="admin-select"
                  value={form.tier}
                  onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
                >
                  <option value="Beta">Beta</option>
                  <option value="Alpha">Alpha</option>
                  <option value="Alpha+">Alpha+</option>
                </select>
              </div>
              <div className="admin-modal-row">
                <label>Report Type</label>
                <input
                  className="admin-input"
                  type="text"
                  value={form.report_type}
                  onChange={(e) => setForm((f) => ({ ...f, report_type: e.target.value }))}
                  placeholder="e.g. Sales"
                />
              </div>
              <div className="admin-modal-row">
                <label>Report UUID</label>
                <input
                  className="admin-input"
                  type="text"
                  value={form.report_uuid}
                  onChange={(e) => setForm((f) => ({ ...f, report_uuid: e.target.value }))}
                  placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                />
              </div>
            </div>
            <div className="admin-modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                disabled={saving}
                onClick={() => !saving && setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                disabled={saving || !form.tier || !form.report_type || !form.report_uuid}
                onClick={async () => {
                  try {
                    setSaving(true);
                    const created = await adminService.addUserReport(user_id, form);
                    const tierMap = {
                      beta: 'Beta',
                      alpha: 'Alpha',
                      'alpha+': 'Alpha+',
                      'alpha-plus': 'Alpha+'
                    };
                    const tierKey = tierMap[String(created.tier || '').toLowerCase()] || 'Beta';
                    setReports((prev) => {
                      const copy = { Beta: [...prev.Beta], Alpha: [...prev.Alpha], 'Alpha+': [...prev['Alpha+']] };
                      if (tierKey === 'Beta') copy.Beta = [...copy.Beta, created];
                      else if (tierKey === 'Alpha') copy.Alpha = [...copy.Alpha, created];
                      else if (tierKey === 'Alpha+') copy['Alpha+'] = [...copy['Alpha+'], created];
                      return copy;
                    });
                    showSuccess('Report added successfully');
                    setShowModal(false);
                    setForm({ tier: 'Beta', report_type: '', report_uuid: '' });
                  } catch (e) {
                    showError(e.message || 'Failed to add report');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={statusModalOpen}
        onClose={() => (!statusUpdating ? setStatusModalOpen(false) : null)}
        onConfirm={async () => {
          if (!user) return;
          try {
            setStatusUpdating(true);
            await adminService.updateUserStatus(user_id, nextStatus);
            setUser((prev) => (prev ? { ...prev, is_active: nextStatus } : prev));
            showSuccess(nextStatus ? 'User activated successfully' : 'User deactivated successfully');
            setStatusModalOpen(false);
          } catch (e) {
            showError(e.message || 'Failed to update user status');
          } finally {
            setStatusUpdating(false);
          }
        }}
        loading={statusUpdating}
        title={nextStatus ? 'Activate user?' : 'Deactivate user?'}
        message={
          nextStatus
            ? 'This user will regain access to their reports immediately.'
            : 'The user will lose access to all portals until re-activated. Are you sure you want to continue?'
        }
        confirmText={nextStatus ? 'Activate' : 'Deactivate'}
        icon={nextStatus ? '✅' : '⚠️'}
      />
      {tierModalOpen && (
        <div className="admin-modal-overlay" onClick={() => !tierUpdating && setTierModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="admin-modal-header">
              <div>Change Product Tier</div>
              <button className="admin-btn admin-btn-secondary" onClick={() => !tierUpdating && setTierModalOpen(false)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                <div style={{ fontWeight: '600', marginBottom: '4px', color: '#92400e' }}>⚠️ Important: Subscription Change</div>
                <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
                  Changing the product tier will immediately affect the user's access to dashboard reports:
                  <ul style={{ marginTop: '8px', marginLeft: '20px', padding: 0 }}>
                    <li><strong>B:</strong> Access to Beta reports only</li>
                    <li><strong>A:</strong> Access to Beta and Alpha reports</li>
                    <li><strong>A+:</strong> Access to all reports (Beta, Alpha, Alpha+)</li>
                  </ul>
                </div>
              </div>
              <div className="admin-modal-row">
                <label>Current Tier</label>
                <div style={{ 
                  padding: '6px 12px', 
                  borderRadius: '4px', 
                  backgroundColor: (user?.product_tier || 'B') === 'A+' ? '#10b981' : (user?.product_tier || 'B') === 'A' ? '#3b82f6' : '#6b7280',
                  color: 'white',
                  fontWeight: '500',
                  display: 'inline-block'
                }}>
                  {user?.product_tier || 'B'}
                </div>
              </div>
              <div className="admin-modal-row">
                <label>New Tier</label>
                <select
                  className="admin-select"
                  value={nextTier}
                  onChange={(e) => setNextTier(e.target.value)}
                  disabled={tierUpdating}
                >
                  <option value="B">B - Beta Only</option>
                  <option value="A">A - Beta & Alpha</option>
                  <option value="A+">A+ - All Tiers</option>
                </select>
              </div>
            </div>
            <div className="admin-modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                disabled={tierUpdating}
                onClick={() => !tierUpdating && setTierModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                disabled={tierUpdating || nextTier === (user?.product_tier || 'B')}
                onClick={() => {
                  if (!user || nextTier === (user.product_tier || 'B')) return;
                  setTierModalOpen(false);
                  setTierConfirmModalOpen(true);
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={tierConfirmModalOpen}
        onClose={() => (!tierUpdating ? setTierConfirmModalOpen(false) : null)}
        onConfirm={async () => {
          if (!user) return;
          try {
            setTierUpdating(true);
            await adminService.updateUserTier(user_id, nextTier);
            setUser((prev) => (prev ? { ...prev, product_tier: nextTier } : prev));
            showSuccess(`User tier updated to ${nextTier} successfully`);
            setTierConfirmModalOpen(false);
          } catch (e) {
            showError(e.message || 'Failed to update user tier');
          } finally {
            setTierUpdating(false);
          }
        }}
        loading={tierUpdating}
        title="Confirm Tier Change"
        message={
          `Are you sure you want to change ${user?.full_name || user?.username || 'this user'}'s product tier from ${user?.product_tier || 'B'} to ${nextTier}?\n\n` +
          `This will immediately affect their access:\n` +
          (nextTier === 'B' ? '• Access to Beta reports only' : '') +
          (nextTier === 'A' ? '• Access to Beta and Alpha reports' : '') +
          (nextTier === 'A+' ? '• Access to all reports (Beta, Alpha, Alpha+)' : '') +
          `\n\nThis action cannot be easily undone.`
        }
        confirmText="Confirm Change"
        icon="⚠️"
      />
      </div>
    </SidebarLayout>
  );
};

export default AdminUserDetails;


